import React, { useState, useRef } from 'react';
import { Modal, Upload, Button, message } from 'antd';
import { DollarOutlined, PictureOutlined } from '@ant-design/icons';
import FinansDataTable from '../shared/FinansDataTable';
import PaymentProcessModal from '../shared/PaymentProcessModal';
import { useFinanceApi } from '../api/financeApi';
import {
  paymentColumns, collectionColumns, recurringColumns, paymentRequestColumns,
  cariColumns, transactionTypeColumns, companyColumns, accountColumns,
} from './tableConfigs';

// --- Ödemeler ---
export function OdemelerTab({ lookups, perms }) {
  return (
    <FinansDataTable
      resource="payments" title="Ödeme" columns={paymentColumns}
      lookups={lookups} perms={perms.odemeler || {}}
      filters={['date', 'firma', 'doviz', 'search']}
    />
  );
}

// --- Tahsilatlar ---
export function TahsilatlarTab({ lookups, perms }) {
  return (
    <FinansDataTable
      resource="collections" title="Tahsilat" columns={collectionColumns}
      lookups={lookups} perms={perms.tahsilatlar || {}}
      filters={['date', 'firma', 'doviz', 'search']}
    />
  );
}

// --- Düzenli Ödemeler ---
export function DuzenliOdemelerTab({ lookups, perms }) {
  return (
    <FinansDataTable
      resource="recurring" title="Düzenli Ödeme" columns={recurringColumns}
      lookups={lookups} perms={perms.duzenli_odemeler || {}}
      filters={['date', 'firma', 'doviz', 'search']}
    />
  );
}

// --- Ödeme Talebi (+ "Öde" akışı) ---
export function OdemeTalebiTab({ lookups, perms }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [processRow, setProcessRow] = useState(null);
  const p = perms.odeme_talebi || {};

  const rowActions = [{
    label: 'Öde',
    icon: <DollarOutlined />,
    show: () => p.edit !== false,
    onClick: (record) => setProcessRow(record),
  }];

  return (
    <>
      <FinansDataTable
        resource="payment-requests" title="Ödeme Talebi" columns={paymentRequestColumns}
        lookups={lookups} perms={p} rowActions={rowActions} refreshKey={refreshKey}
        filters={['date', 'firma', 'doviz', 'search']}
      />
      <PaymentProcessModal
        row={processRow} lookups={lookups}
        onClose={() => setProcessRow(null)}
        onDone={() => { setProcessRow(null); setRefreshKey((k) => k + 1); }}
      />
    </>
  );
}

// --- Cari (+ logo) ---
export function CariTab({ lookups, perms }) {
  const api = useFinanceApi();
  const [logoRow, setLogoRow] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const p = perms.cari || {};

  const rowActions = [{
    label: 'Logo',
    icon: <PictureOutlined />,
    show: () => p.edit !== false,
    onClick: (record) => setLogoRow(record),
  }];

  const beforeUpload = async (file) => {
    if (!file.type.startsWith('image/')) { message.error('Sadece resim'); return Upload.LIST_IGNORE; }
    if (file.size > 2 * 1024 * 1024) { message.error('2MB sınırı'); return Upload.LIST_IGNORE; }
    try {
      await api.uploadLogo(logoRow.id, file);
      message.success('Logo yüklendi');
      setLogoRow(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Yüklenemedi');
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <>
      <FinansDataTable
        resource="cari" title="Cari" columns={cariColumns}
        lookups={lookups} perms={p} rowActions={rowActions} refreshKey={refreshKey}
        filters={['firma', 'active', 'search']}
      />
      <Modal title={`Logo Yükle — ${logoRow?.ad || ''}`} open={!!logoRow}
        footer={null} onCancel={() => setLogoRow(null)} destroyOnClose>
        <Upload.Dragger beforeUpload={beforeUpload} showUploadList={false} accept="image/*" maxCount={1}>
          <p><PictureOutlined style={{ fontSize: 32 }} /></p>
          <p>Logoyu buraya sürükleyin veya tıklayın (maks. 2MB)</p>
        </Upload.Dragger>
        {logoRow?.logo && <p style={{ marginTop: 12 }}>Mevcut: {logoRow.logo}</p>}
      </Modal>
    </>
  );
}

// --- İşlem Türleri ---
export function IslemTuruTab({ lookups, perms }) {
  return (
    <FinansDataTable
      resource="transaction-types" title="İşlem Türü" columns={transactionTypeColumns}
      lookups={lookups} perms={perms.islem_turu || {}}
      filters={['active', 'search']}
    />
  );
}

// --- Firmalar ---
export function FirmaTab({ lookups, perms }) {
  return (
    <FinansDataTable
      resource="companies" title="Firma" columns={companyColumns}
      lookups={lookups} perms={perms.firma || {}}
      filters={['active', 'search']}
    />
  );
}

// --- Banka Hesapları ---
export function BankaHesapTab({ lookups, perms }) {
  return (
    <FinansDataTable
      resource="accounts" title="Banka Hesabı" columns={accountColumns}
      lookups={lookups} perms={perms.banka_hesap || {}}
      filters={['firma', 'doviz', 'active', 'search']}
    />
  );
}
